import { AIConversation, AKMeta, AppStateItem, BaseItem, ChatMessageItem, ChatMessageItemTypes, ChatMessageUpdate, ChatMessageUpdateTypes, ContentPart, ErrorItem, MessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import { ConversationItemContent, RealtimeClientEvent, ResponseCreateEvent, ConversationItem, RealtimeServerEvent, ResponseAudioTranscriptDoneEvent, ResponseTextDoneEvent, ConversationItemWithReference } from 'openai/resources/beta/realtime/realtime';
import React, { ReactNode, Reducer, useCallback, useMemo, useRef, useContext, useEffect, useReducer, useState, PropsWithChildren, PropsWithRef } from "react";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { WebRTCContext } from "./WebRTCContext";
import slugid from 'slugid';
import { v4 } from 'uuid';
import { safeParse, useLogger } from "../Util";
import zod from 'zod';
import { zodResponseFormat } from "openai/helpers/zod";
import { ChatCompletionAssistantMessageParam, ChatCompletionMessageParam, ChatCompletionTool, ChatCompletionToolMessageParam } from "openai/resources";
import InterfaceContext from "../Context";
import { conversationIdCookieName } from "./ChatSessionContext";
import { captureException } from "@sentry/react";
import { useCookies } from "react-cookie";
import { AICoachState, useAICoach } from "../AICoach/AICoachContext";

export type ChatContextType = {

  /**
   * The running chat history of the conversation so far
   */
  messages: ChatMessageItem[],

  /**
   * The most recently updated chat message item
   */
  latestUpdatedItem?: ChatMessageItem,

  /**
   * Any tool calls that are actively unfulfilled.
   * These values correspond to the related indicies in the `message` field.
   */
  toolCalls?: Set<number>,

  /**
   * Any errors that have occurred during the chat.
   * Any of these may cause the chat to terminate/restart.
   */
  errors?: ErrorItem[],

  /**
   * Sends a message to the chat
   */
  sendMessage(message: ChatMessageUpdate, opts?: { changeMessageId?: string, createResponse?: boolean, modalities?: ['audio', 'text'] | ['text'] }): void,

  /**
   * Sets context that is used in the template, rather than tracked as a conversation item.
   */
  setContext(key: string, context: MessageItem | AppStateItem): void,

  /**
   * Restarts the chat with a blank slate.
   * This includes eliminating any history and reconnecting (if a live connection is required, like WebRTC)
   */
  reset(): void,

  /**
   * Allows restoring the chat history back to a specific set of messages.
   * This can also be used to restore history from a previous session.
   */
  restore(messages: ChatMessageItem[]): void,

  /**
   * Indicates the chat is capable of being active and ready.
   * This is the first flag that will flip when a chat is enabled,
   * but shortly after the `active` and `ready` flags should also be flipped.
   */
  enabled: boolean,

  /**
   * Indicates the chat is actively operational.
   */
  active: boolean,

  /**
   *  Indicates the chat is ready for input
   */
  ready: boolean,

  /**
   * Shows the current turn of the chat.
   * "listening" indicates its waiting for input (highly correlated to ready flag)
   * "speaking" indicates the assistant is currently replying
   * "thinking" indicates the assistant is constructing a reply
   */
  phase: 'unknown' | 'listening' | 'speaking' | 'thinking',

  /**
   * Registers a tool that the chat assistant may decide to use when prompted in specific ways.
   */
  registerTool<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>): void,

  /**
   * The currently registered tools that the chat assitant knows about.
   */
  tools: Record<string, Tool<any, any>>,

  /**
   * Enables the chat,
   * which leads to the activation and ready-ing of the chat as well.
   */
  setEnabled(enable: boolean): void,

  /* ID of the conversation */
  conversationId?: string

  /**
   * A version number to track "milestone" changes to chat state
   */
  version: number
  
  /**
   * Handle API errors
   */
  handleAPIError?: (error?: string) => boolean
};

const defaultConversationState = {
  messages: [],
  errors: [],
  toolCalls: new Set<number>(),
  version: 0,
  dispatch: () => { console.warn('no chat conversation context setup, ignoring dispatch') }
}
export type ChatConversationContextType = Pick<ChatContextType, "messages" | "errors" | "toolCalls" | "version"> & {
  dispatch: React.Dispatch<ChatMessageUpdate | { type: 'clear-chat' }>
};
export const ChatConversationContext = React.createContext<ChatConversationContextType>(defaultConversationState);
export const ChatConversationProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(ChatMessageReducer, { version: 0, list: [], map: new Map(), errors: [], toolCalls: new Set<number>() });
  const { list, ...rest } = state;
  const conversationState = {
    messages: list,
    dispatch,
    ...rest
  };

  return <ChatConversationContext.Provider value={conversationState}>
    {children}
  </ChatConversationContext.Provider>
}

export const ChatContext = React.createContext<ChatContextType>({
  ...defaultConversationState,
  phase: 'unknown',
  active: false,
  ready: false,
  enabled: false,
  tools: {},
  version: 0,
  reset() { console.warn('no chat context setup, ignoring reset'); },
  restore() { console.warn('no chat context setup, ignoring restore'); },
  sendMessage() { console.warn('no chat context setup, ignoring message'); },
  registerTool() { console.warn('no chat context setup, ignoring registerTool'); },
  setEnabled() { console.warn('no chat context setup, ignoring setEnabled'); },
  setContext() { console.warn('no chat context setup, ignoring setContext'); }
});

export const ChatProvider = ({ children, modality }: PropsWithChildren & Pick<AICoachState, "modality">) => {
  const isAudio = modality === "audio";
  const voice = useOpenAIVoiceChat();
  const text = useOpenAITextChat(!isAudio);
  return <ChatContext.Provider value={isAudio ? voice : text}>
    {children}
  </ChatContext.Provider>;
};

export type Tool<T extends zod.ZodObject<any>, Out> = {
  name: string,
  description: string,
  schema: T,
  fn: (args: zod.infer<T>) => Promise<Out>
};

export type ControlSignal = { type: 'clear-chat' } | { type: 'update-item-meta', id: string, update: Partial<AKMeta>, incrementVersion?: boolean };
export type AudioChatEvent = RealtimeServerEvent | RealtimeClientEvent | ControlSignal;

export const AudioChatStateReducer: Reducer<ChatState, AudioChatEvent> = (state, action) => ChatMessageReducer(state, AudioChatAdapter(action));

type ChatState = {
  version: number,
  map: Map<string, number>,
  toolCalls: Set<number>,
  list: ChatMessageItem[],
  errors?: ErrorItem[],
  latestUpdatedItem?: ChatMessageItem
};

/**
 * Sometimes OpenAI Realtime responds with json/pseudo JSON, and sometimes not...
 * This attempts to parse it if it's JSON, and it cleans up non-meaningful characters
 * @param s
 */
function chatContentToText(s: string) {
  const json = safeParse(s, null);
  return (json?.message ?? json?.response ?? s).replaceAll(/^(\{|\")+|(\"|\})+$/g, "");
}

function useTools({ sendMessage, toolCalls, messages, tools }: Pick<ChatContextType, 'sendMessage' | 'toolCalls' | 'tools' | 'messages'>) {
  useAsyncEffect(async () => {
    for (const id of Array.from(toolCalls ?? [])) {
      const message = messages[id];

      if (!message) {
        console.error('[AI CHAT] no message found for function call to reference.');
        continue;
      } else if (message.type !== 'tool_call') {
        console.error('[AI CHAT] somehow a non-function-call id got mapped to a function call arg');
        continue;
      } else if (!message.name) {
        console.error('[AI CHAT] no tool name specified in original message');
        continue;
      }

      const tool = tools[message.name];

      if (!tool) {
        console.error('no tool found with the name', message.name);
        continue;
      }

      if (!message.arguments) {
        console.error('[AI CHAT] no arguments provided for function call', message);
        continue;
      }

      // using a symbol guarantees a unique identifier for a bad outcome
      const badResultSymbol = Symbol.for('BadArgFormat');
      const args = safeParse(message.arguments, badResultSymbol);

      if (args === badResultSymbol) {
        console.error('[AI CHAT] failed to parse arguments from:', message.arguments);
        continue;
      }

      console.log('[AI CHAT] executing tool call', tool.name, 'with arguments', args);

      let result: any;
      try {
        result = await tool.fn(args);
      } catch (err: any) {
        result = { error: err?.message || err };
      }

      console.debug('[AI CHAT] tool call result:', result, JSON.stringify(result));

      sendMessage({
        id: slugid.encode(v4()),
        previousItemId: message.id,
        type: 'tool_call_output',
        callId: message.callId,
        output: JSON.stringify(result),
      }, { createResponse: true });
    }
  }, [toolCalls, tools, messages]);
}

export function useChat() {
  return useContext(ChatContext);
}

export function useOpenAITextChat(enabled?: boolean): ChatContextType {
  // const [state, dispatch] = useReducer(ChatMessageReducer, { version: 0, list: [], map: new Map(), errors: [], toolCalls: new Set<number>() });
  const chat = usePost('/ai/chat/message');
  const [phase, setPhase] = useState<'listening' | 'speaking' | 'thinking' | 'unknown'>('listening');
  const [toolDefinitions, setToolDefinitions] = useState<Record<string, Tool<zod.ZodObject<any>, any>>>({});
  const [cookies, setCookie, removeCookie] = useCookies([conversationIdCookieName]);
  const chatRequest = useRef<ReturnType<typeof chat> | null>(null);
  const [chatContext, setChatContext] = useState<Record<string, MessageItem | AppStateItem>>({});
  const {
    dispatch,
    ...state
  } = useContext(ChatConversationContext);
  const chatContextRef = useRef(chatContext);

  const conversationId = cookies[conversationIdCookieName];
  
  const registeredTools = useMemo(() => Object.values(toolDefinitions).map(tool => {
    const { schema } = zodResponseFormat(tool.schema, 'schema').json_schema;
    return {
      type: 'function',
      function: {
        parameters: schema,
        name: tool.name,
        description: tool.description
      }
    } as ChatCompletionTool
  }), [toolDefinitions]);

  const reset = useCallback(() => {
    dispatch({ type: 'clear-chat' });
  }, [dispatch]);

  const restore = useCallback((items: ChatMessageItem[]) => {
    for (const item of items) {
      dispatch(item);
    }
  }, [dispatch]);

  const sendMessage = useCallback((message: ChatMessageUpdate, opts?: { changeMessageId?: string, createResponse?: boolean, modalities?: ['audio', 'text'] | ['text'] }) => {
    dispatch(message);
  }, [dispatch]);

  const lastMessage = useMemo(() => state.messages[Math.max(state.messages.length - 1, 0)], [state.messages]);
  
  const handleAPIError = useCallback((error?:string) => { 
    if (error && /invalid conversation id/ig.test(error)) {
      removeCookie(conversationIdCookieName);
      reset();
      return true;
    }    
    return false;
  }, [reset])

  useAsyncEffect(async (cancel) => {
    if (!enabled) return;

    if (!conversationId) {
      console.error("Conversation ID not found");
      return;
    }

    let cancelled = false;
    cancel.addEventListener("abort", function onAbort() {
      cancelled = true;
    });

    if (lastMessage?.type === 'tool_call_output' || lastMessage?.meta?.createResponse || (lastMessage?.type === 'message' && lastMessage?.role === 'user')) {
      setPhase('thinking');

      try {
        const messages: ChatCompletionMessageParam[] = [];

        for (const item of state.messages) {
          switch (item.type) {
            case 'appState':
              messages.push({
                role: 'system',
                content: [{
                  type: 'text',
                  text: JSON.stringify(item.appState)
                }]
              });
              break;
            case 'message':
              messages.push({
                role: item.role,
                content: item.content.map(c => ({
                  type: c.type,
                  text: c.content
                }))
              });
              break;
            case 'tool_call':
              messages.push({
                role: 'assistant',
                tool_calls: [{
                  id: item.id,
                  type: 'function',
                  function: {
                    name: item.name,
                    arguments: item.arguments
                  }
                }]
              } as ChatCompletionAssistantMessageParam);
              break;
            case 'tool_call_output':
              messages.push({
                role: 'tool',
                tool_call_id: item.callId,
                content: item.output
              } as ChatCompletionToolMessageParam);
              break;
            default:
              break;
          }
        }

        chatRequest.current = chat({
          conversationId,
          newMessages: [lastMessage],
          appState: chatContextRef.current.appState as AppStateItem
        }, cancel);

        const chatResponse = await chatRequest.current;

        // if request was cancelled, just exit
        if (cancelled) {
          return;
        }
        chatRequest.current = null;

        if (!chatResponse || "error" in chatResponse) {
          const handled = handleAPIError((chatResponse as any)?.error);
          if (!handled) {
            throw new Error((chatResponse as any)?.error ?? "No response from the chat");
          }
        }

        for (const messageItem of chatResponse) {
          dispatch(messageItem);
        }

        setPhase('speaking');
      } catch (e: any) {
        chatRequest.current = null;
        dispatch({
          type: 'error',
          error: {
            type: 'request_error',
            message: e.message
          }
        });
        captureException(e, {
          ...e,
          conversationId
        });
      } finally {
        if (!chatRequest.current) {
          setPhase('listening');
        }
        chatRequest.current = null;
      }
    }
  }, [lastMessage, conversationId, handleAPIError]);

  const registerTool = useCallback(<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>) => {
    setToolDefinitions(prev => ({
      ...prev,
      [tool.name]: tool as unknown as Tool<zod.ZodObject<any>, any>
    }));
  }, [setToolDefinitions]);

  useTools({
    sendMessage,
    messages: state.messages,
    toolCalls: state.toolCalls,
    tools: toolDefinitions
  });
  
  const setContext = useCallback((key: string, value: MessageItem) => {
    setChatContext(prev => {
      const updatedChatContext = {
        ...prev,
        [key]: value
      };
      chatContextRef.current = updatedChatContext;
      return updatedChatContext;
    });
  }, [setChatContext]);

  return {
    ...state,
    ready: phase === 'listening',
    phase,
    reset,
    restore,
    registerTool,
    sendMessage,
    conversationId,
    tools: toolDefinitions,
    active: true,
    enabled: true,
    setEnabled() { },
    setContext,
    handleAPIError
  };
}

export function useOpenAIVoiceChat(): ChatContextType {
  const { lang } = useContext(InterfaceContext);
  const webrtc = useContext(WebRTCContext);
  const tools = useRef<Record<string, Tool<zod.ZodObject<any>, any>>>({});
  const [responding, setResponding] = useState(false);
  const [chatContext, setChatContext] = useState<Record<string, MessageItem>>({});
  const {
    dispatch,
    ...state
  } = useContext(ChatConversationContext);

  const nextResponse = useRef<ConversationItemWithReference[]>([]);

  useEffect(() => {
    const contextItems: ConversationItemWithReference[] = Object.entries(chatContext)
      .map(([k, c]) => ({
        type: 'message',
        role: c.role,
        content: c.content.map(p => ({
          type: c.role === 'assistant' ? 'text' : 'input_text',
          text: k + ': ' + p.content
        }))
      }));

    const conversation: ConversationItemWithReference[] = state.messages
      .map(({ id }) => ({ id, type: 'item_reference' }));

    nextResponse.current = conversation.concat(contextItems);

  }, [chatContext, state.messages]);

  const eventCallback = useCallback((event: RealtimeServerEvent | RealtimeClientEvent) => {
    switch (event.type) {
      case 'input_audio_buffer.speech_started':
        if (responding) {
          setResponding(false);
          webrtc.sendEvent({
            type: 'response.cancel'
          });
        }
        break;
      case 'input_audio_buffer.speech_stopped':
        if (!responding) {
          setResponding(true);
          webrtc.sendEvent({
            type: 'response.create',
            response: {
              input: nextResponse.current?.length ? [
                ...nextResponse.current,
                { type: 'item_reference', id: event.item_id }
              ] : undefined
            }
          });
        }
        break;
      case 'response.done':
        setResponding(false);
        break;
      default:
        break;
    }
    dispatch(AudioChatAdapter(event));
  }, [webrtc.sendEvent, setResponding]);

  useEffect(() => {
    const unsubToken = webrtc.subscribe(
      eventCallback,
      initial => {
        dispatch({ type: 'clear-chat' });
        initial.forEach(e => dispatch(AudioChatAdapter(e)))
      }
    );
    return () => unsubToken();
  }, [eventCallback, webrtc.subscribe, dispatch]);

  const reset = useCallback(() => {
    webrtc.setEnabled(false);
    dispatch({ type: 'clear-chat' });
    setTimeout(() => webrtc.setEnabled(true), 100);
  }, []);

  // setting the audio transcription language improves accuracy and latency
  // but it will signal Realtime to only transcribe in that language.
  // this means that if a user speaks in a language different than what we have specified,
  // Realtime will fail to respond in their spoken language
  useEffect(() => {
    if (webrtc.active && webrtc.realtimeSession?.id) {
      webrtc.updateSession({
        input_audio_transcription: {
          ...webrtc.realtimeSession.input_audio_transcription,
          language: lang,
          prompt: webrtc.realtimeSession.input_audio_transcription?.prompt ?? ""
        }
      })
    }
  }, [lang, webrtc.realtimeSession?.id, webrtc.active]);

  const sendMessage = useCallback((message: ChatMessageUpdate, opts?: { changeMessageId?: string, createResponse?: boolean, modalities?: ['audio', 'text'] | ['text'] }) => {
    if (message.type === 'ignore') return;

    const id = opts?.changeMessageId ?? message.id?.substring(0, 32) ?? slugid.encode(v4());

    switch (message.type) {
      case 'update_meta':
        dispatch(message);
        return;
      case 'tool_call':
        // this is specifically to account 
        // for when we are backfilling
        // from a previous session.
        webrtc.sendEvent({
          type: "conversation.item.create",
          item: {
            id,
            type: "function_call",
            call_id: message.callId,
            name: message.name,
            arguments: message.arguments
          }
        });
        break;
      case 'tool_call_output':
        webrtc.sendEvent({
          type: "conversation.item.create",
          previous_item_id: message.previousItemId,
          item: {
            id,
            type: "function_call_output",
            call_id: message.callId,
            output: message.output
          }
        });
        break;
      case 'appState':
        break;
      case 'message': {
        const content: ConversationItemContent[] = []
        for (const c of message.content) {
          switch (c.type) {
            case 'text':
              content.push({ type: message.role === 'assistant' ? 'text' : 'input_text', text: c.content });
              break;
            default: break;
          }
        }

        // also send message to remote
        webrtc.sendEvent({
          type: "conversation.item.create",
          item: {
            id,
            type: "message",
            role: message.role,
            content
          }
        });

        break;
      }
      case 'error':
        return;
    }

    if (opts?.createResponse || message.meta?.createResponse) {
      const response: ResponseCreateEvent = {
        type: "response.create",
        response: {
          modalities: opts?.modalities,
          input: nextResponse.current?.length ? [
            ...nextResponse.current,
            { type: 'item_reference', id: message.id }
          ] : undefined
        }
      };

      if (responding) {
        webrtc.sendEvent({
          type: 'response.cancel'
        });
        setResponding(false);
      }
      webrtc.sendEvent(response);
    }
  }, [webrtc.sendEvent, responding]);

  const restore = useCallback((items: ChatMessageItem[]) => {
    for (const item of items) {
      dispatch(item);
      const selectedMeta = { ...(item.meta ?? {}) };
      delete selectedMeta['createResponse'];
      sendMessage({ ...item, meta: selectedMeta });
    }
  }, [dispatch, sendMessage]);

  const registerTool = useCallback(<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>) => {
    const newTools = { ...tools.current, [tool.name]: tool as unknown as Tool<zod.ZodObject<any>, any> };

    tools.current = newTools;

    if (webrtc.active) {
      webrtc.sendEvent({
        type: 'session.update',
        session: {
          tools: Object.values(newTools).map(tool => {
            const { schema } = zodResponseFormat(tool.schema, 'schema').json_schema;
            return {
              type: 'function',
              parameters: schema,
              name: tool.name,
              description: tool.description
            }
          })
        }
      });
    }
  }, [webrtc.active, webrtc.sendEvent, tools]);

  useTools({
    sendMessage,
    messages: state.messages,
    toolCalls: state.toolCalls,
    tools: tools.current
  });

  const setContext = useCallback((key: string, msg: MessageItem | AppStateItem) => {
    if(msg.type === 'appState') {
      // voice mode cannot handle a complete app state yet,
      // so we just no-op here to ensure it isn't used.
      return;
    }
    setChatContext(prev => ({
      ...prev,
      [key]: msg
    }));
  }, [setChatContext]);

  return {
    messages: state.messages,
    phase: webrtc.phase,
    version: state.version,
    reset,
    restore,
    ready: !responding,
    sendMessage,
    registerTool,
    tools: tools.current,
    enabled: webrtc.enabled,
    setEnabled: webrtc.setEnabled,
    active: webrtc.active,
    setContext
  }
}

/**
 * This reducer drives the chat state in its entirety (audio and text).
 */
export const ChatMessageReducer: Reducer<ChatState, ChatMessageUpdate | { type: 'clear-chat' }> = ({ map, list, toolCalls, errors, version, latestUpdatedItem }, action) => {
  // call this function for any changes which should trigger persisting state
  function incrementVersion() {
    if (!(action as BaseItem).silent) {
      version = (version ?? 0) + 1;
    }
  }

  function updateItemMeta(itemId: string, update: Partial<AKMeta>) {
    const itemIndex = map.get(itemId);
    if (typeof itemIndex === 'number') {
      const itemToUpdate = list[itemIndex];
      itemToUpdate.meta = {
        time: Date.now(),
        ...itemToUpdate.meta,
        ...update
      };
      latestUpdatedItem = itemToUpdate;
    }
  }

  function upsertItem<T extends ChatMessageUpdate, E = T['type'] extends ChatMessageItem['type'] ? ChatMessageItemTypes[T['type']] : ChatMessageItem>(
    id: string,
    item: T,
    merge: (item: E, update: T) => E): [number, E] {

    const isUpdate = map.has(id);
    if (!isUpdate) {
      map.set(id, list.length);
    } 

    const index = map.get(id)!;

    const existing = list[index] ?? {};

    const updatedItem = merge(existing as Parameters<typeof merge>[0], item);
    if (isUpdate) { 
      latestUpdatedItem = updatedItem as ChatMessageItem;
    }

    (list[index] as E) = {
      id,
      ...updatedItem
    };

    return [index, list[index] as E];
  }

  function appendError(error: ErrorItem) {
    return (errors || []).concat(error);
  }

  switch (action.type) {

    case "clear-chat":
      return {
        version: 0,
        map: new Map(),
        list: [],
        toolCalls: new Set()
      };

    case "update_meta":
      upsertItem(action.id, action, (existing, update) => ({
        id: update.id,
        role: 'user' as 'user',
        content: [],
        ...existing,
        meta: {
          time: Date.now(),
          ...existing.meta,
          ...update.meta,
        }
      }));
      incrementVersion();
      break;

    case "content_delta":
      upsertItem<ChatMessageUpdateTypes['content_delta'], MessageItem>(action.id, action, (existing, update) => {

        existing.content[update.index] =
          update.overwrite ?
            update.content :
            { type: 'text', content: (existing.content?.[update.index]?.content || '') + update.content.content };

        return existing;
      });
      incrementVersion();
      break;

    case "appState":
      // Don't do anything for appState, for now.
      break;
    case "message":
      upsertItem(action.id, action, (existing, update) => {

        const content: ContentPart[] = [];
        const contentSize = Math.max(existing.content?.length || 0, update.content?.length || 0);
        for (let i = 0; i < contentSize; i++) {
          content[i] = {
            type: 'text',
            content: update.content?.[i]?.content || existing.content?.[i]?.content || ''
          };
        }

        return {
          ...existing,
          ...update,
          content,
          meta: {
            fromURL: window.location.pathname,
            ...existing.meta,
            ...(update.meta ?? {}),
            time: existing.meta?.time ?? update.meta?.time ?? Date.now(),
          }
        };
      });
      incrementVersion();
      break;
    case "tool_call":
      const [itemIndex, updated] = upsertItem(action.id, action, (existing, update) => {
        return {
          ...existing,
          ...update,
          name: update.name || existing.name,
          arguments: update.arguments || existing.arguments,
          callId: update.callId || existing.callId,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...(update.meta ?? {})
          }
        };
      });
      if (updated.name && updated.arguments && updated.meta?.phase !== 'done') {
        map.set(updated.callId, itemIndex);
        toolCalls.add(itemIndex);
        // prevents additional calls from happening for the same tool call, 
        // mostly for after restoring from a previous conversation.
        updateItemMeta(action.id, {
          phase: 'done'
        });
      }
      break;
    case "tool_call_output":
      upsertItem(action.id, action, (existing, update) => {
        return {
          ...existing,
          ...update,
          output: update.output || existing.output,
          callId: update.callId || existing.callId,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...(update.meta ?? {})
          }
        };
      });
      const index = map.get(action.callId);
      if (typeof index === 'number') {
        map.delete(action.callId);
        toolCalls.delete(index);
      }
      break;
    case "error":
      errors = appendError(action);
      incrementVersion();
      break;
    case "remove_item": {
      const index = map.get(action.id);
      if (typeof index === 'number') {
        map.delete(action.id);
        list.splice(index, 1);
      }
      incrementVersion();
      break;
    }
    case "update_item": {
      upsertItem(action.id, action.with, (_, update) => update);
      incrementVersion();
      break;
    }
  }

  return {
    version,
    map: new Map(map),
    list: Array.from(list),
    toolCalls: new Set(toolCalls),
    errors,
    latestUpdatedItem
  };
};

function AudioChatAdapter(action: AudioChatEvent): ChatMessageUpdate {

  const noItemId = () => ({
    type: 'error',
    error: {
      type: 'adapter_internal_error',
      code: 'missing_item_id',
      message: `missing an item id for the ${action.type}`
    },
    sourceObj: action
  } as ErrorItem);

  switch (action.type) {

    case "conversation.item.create":
    case "conversation.item.created":
    case "response.output_item.added":

      if (!action.item.id) return noItemId();

      const meta: AKMeta = {
        version: 0,
      };

      switch (action.item.type) {
        case 'message':
          return {
            id: action.item.id,
            type: 'message',
            content: action.item?.content?.map(c => ({ type: 'text', content: c.text! })) ?? [],
            role: action.item.role || 'user',
            meta
          };
        case 'function_call':
          return {
            id: action.item.id,
            type: 'tool_call',
            arguments: action.item.arguments || '',
            callId: action.item.call_id || '[no call_id]',
            name: action.item.name || '[no function name]',
            meta
          }
        case 'function_call_output':
          return {
            id: action.item.id,
            type: 'tool_call_output',
            callId: action.item.call_id || '[no call_id]',
            output: action.item.output || '[no output]',
            meta
          }
        default:
          return {
            type: 'error',
            error: {
              type: 'unsupported_item_type',
              message: `unsupported type found: ${action.item.type}`
            },
            sourceObj: action
          };
      }

    case "input_audio_buffer.speech_started":
      return {
        type: 'ignore'
      };

    case "input_audio_buffer.speech_stopped":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          phase: "pending",
          version: 1
        }
      };

    // remote added a new response
    case "conversation.item.input_audio_transcription.delta":
    case "response.audio_transcript.delta":
    case "response.text.delta": {
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index ?? 0,
        content: { type: 'text', content: action.delta || '' },
        sourceObj: action
      }
    }
    case "response.output_item.done":
      if (!action.item.id) return noItemId();
      // remote has completed the response
      return {
        id: action.item.id,
        type: 'update_meta',
        meta: {
          phase: "done"
        }
      };
    case "conversation.item.input_audio_transcription.completed":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          phase: "done"
        }
      };
    case "response.text.done":
    case "response.audio_transcript.done": {
      let completedText = (action as ResponseTextDoneEvent).text;
      let completedTranscript = (action as ResponseAudioTranscriptDoneEvent).transcript;
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index,
        overwrite: true,
        content: { type: 'text', content: completedText ?? completedTranscript },
        meta: {
          phase: 'done'
        }
      }
    }
    case "error":
      return {
        type: 'error',
        error: {
          code: action.error.code,
          type: action.type,
          message: action.error.message
        }
      }
    case "response.function_call_arguments.done": {
      return {
        id: action.item_id,
        callId: action.call_id,
        type: 'tool_call',
        arguments: action.arguments
      };
    }
    case "update-item-meta":
      return {
        type: 'update_meta',
        id: action.id,
        meta: action.update as AKMeta
      }
    default:
      return {
        type: 'ignore',
        sourceObj: action
      }
  }
};
